import React, { Component } from 'react';

import _get from 'lodash.get';
import { formatContract } from '@shootsta/contract-and-credit-utilities';
import CreditsContext from '../contexts/creditsContext';
import creditsContextSelector from '../selectors/creditsContextSelector';
import resolveCanSubmit from '../selectors/resolveCanSubmit';
import { withFlagsConsumer } from '../../FeatureFlags';


























const getSelectedTeamIdForSubscriptionsV2 = ({
  creditType,
  teamCreditInfo,
  userTeams,
  hasAdminRole
}) => {
  if (teamCreditInfo.length === 0 || creditType !== 'TEAM') {
    return '';
  }

  const creditInfoForTeams = hasAdminRole ?
  teamCreditInfo :
  teamCreditInfo.filter((it) => Boolean(userTeams.
  find((userTeam) => userTeam.teamId === it.team.teamId)
  ));

  if (!creditInfoForTeams.length) {
    return '';
  }

  const { team: { teamId } } = creditInfoForTeams.find(({ team: {
      creditBalance
    } }) => creditBalance.balance + creditBalance.bufferRemaining > 0) || creditInfoForTeams[0];

  return teamId;
};

class CreditsProvider extends Component {


  constructor() {
    super();

    this.state = {
      userTeams: [],
      creditBalance: {
        balance: 0,
        creditTotal: 0,
        rolloverBalance: 0,
        rolloverTotal: 0
      },
      videoQuantity: 0,
      organisationProduct: [],
      contractItemBalance: [],
      canCreateOrSubmitProject: false,
      financeBalance: null,
      orgTeams: []
    };
  }

  componentDidMount() {
    this.loadCreditsInformation();
    this.setOrgTeams();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  componentWillUnmount() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  // Refresh credit/balance state when the user returns to the tab. This replaces
  // a blind 60s poll that fired four heavy queries (org + teams + two DynamoDB
  // credit-ledger reads) from every open tab forever — the single largest source
  // of backend load. The balance only changes via user/admin actions, which
  // already call onCreditsUpdated; the one remaining staleness case (another
  // actor changing shared/pooled credit while this tab idles) is caught here on
  // re-focus, with zero idle network traffic. The submit path is server-validated
  // regardless, so a momentarily stale gate cannot cause an over-spend.
  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.loadCreditsInformation();
    }
  };

  getContractCredits = async (creditType) => {
    const { flags, organisationId, getContractBalance } = this.props;

    const { subscriptionsV22 = false } = flags || {};

    if (!subscriptionsV22) {
      return {
        isContractCreditsUnlimited: false,
        contractItemBalance: []
      };
    }

    const contractBalanceResponse = await getContractBalance({
      organisationId,
      owners: ['WORKSPACE', 'TEAMS'],
      types: ['WORKSPACE|CREDITS', 'WORKSPACE|TOPUP_CREDITS', 'WORKSPACE|BUFFER_CREDITS']
    });

    const balanceType = creditType === 'SHARE' ? 'ProductBalance' : 'TeamBalance';

    const workspaceCreditsBalance = _get(contractBalanceResponse, 'data.getContractItemBalance', []).
    filter((balance) => balance.__typename === 'ProductBalance').
    find((balance) => balance.type === 'WORKSPACE|CREDITS');

    const isContractCreditsUnlimited = workspaceCreditsBalance ?
    ['Infinity'].includes(workspaceCreditsBalance.value) :
    false;

    const contractItem = _get(contractBalanceResponse, 'data.getContractItemBalance', []);

    const contractItemBalance = contractItem.
    filter((balance) => balance.__typename === balanceType).
    map((balance) => ({
      ...balance,
      value: Number(balance.value)
    }));

    const formattedContracts = formatContract(contractItem, creditType);

    return {
      isContractCreditsUnlimited,
      contractItemBalance,
      ...formattedContracts
    };
  };

  setOrgTeams = async () => {
    const { getTeams } = this.props;
    const { data, error } = await getTeams();

    if (!data || error) {
      return;
    }

    this.setState({ orgTeams: _get(data, 'getTeams', []) });
  };

  // Returns the ledger balance, or null on error so the caller fails closed.
  fetchLedgerBalance = async () => {
    const { getFinanceBalance } = this.props;

    if (!getFinanceBalance) {
      return null;
    }

    const { data, error } = await getFinanceBalance({});

    if (error) {
      return null;
    }

    return _get(data, 'getFinanceBalance', null);
  };

  loadCreditsInformation = async () => {
    const {
      organisationId,
      getOrganisation,
      getUserTeams,
      flags,
      hasAdminRole
    } = this.props;

    if (!organisationId) {return;}

    const [orgResponse, teamsResponse] = await Promise.all(
      [
      getOrganisation(),
      getUserTeams()]

    );

    const invalidOrgData = !orgResponse.data ||
    !orgResponse.data.getOrganisation ||
    orgResponse.error;

    if (invalidOrgData) {
      return;
    }

    const selectedOrganisation = orgResponse.data.getOrganisation;
    const userTeams = _get(teamsResponse, 'data.getUserTeams', []);

    const {
      creditBalance,
      package: proSubscription,
      organisationProduct
    } = selectedOrganisation;

    const { videoQuantity = 0, creditType = 'SHARE' } = proSubscription || {};

    const { subscriptionsV22 = false, financeLedgerEnabled = false } = flags || {};

    // The ledger balance is independent of the contract credits, so fetch both
    // concurrently rather than serially — saves a full round-trip per poll.
    const [contractCredits, financeBalance] = await Promise.all([
      this.getContractCredits(creditType),
      financeLedgerEnabled ? this.fetchLedgerBalance() : Promise.resolve(null)
    ]);

    const {
      isContractCreditsUnlimited = false,
      contractItemBalance = [],
      teamCredits: teamContractCredits = [],
      orgCredits: orgContractCredits = {}
    } = contractCredits;

    const isUnlimited = subscriptionsV22 ? isContractCreditsUnlimited : videoQuantity === -1;

    const teamCreditInfo = subscriptionsV22 ?
    teamContractCredits :
    userTeams.reduce((acc, team) => [...acc, { team }], []);

    const orgCreditInfo = subscriptionsV22 ?
    orgContractCredits :
    selectedOrganisation;

    const selectedTeamId = getSelectedTeamIdForSubscriptionsV2({
      creditType,
      teamCreditInfo,
      hasAdminRole,
      userTeams
    });

    // creditCost is intentionally omitted here. This is the global "create
    // project" gate, evaluated before a project type — and therefore its true
    // credit cost — is known, so it defaults to 1 (the cheapest project). That
    // is the most permissive coarse check; the precise per-cost gate lives in
    // PaymentsInvoice, which knows the project's real creditCost.
    const canSubmit = resolveCanSubmit({
      financeLedgerEnabled,
      isUnlimited,
      creditType,
      selectedTeamId,
      financeBalance,
      // Enable creation whenever any team or the pool has credit.
      teamAgnostic: true,
      teamCreditInfo,
      orgCreditInfo,
      isNewSubscriptionModelEnabled: subscriptionsV22
    });

    this.setState({
      userTeams,
      isUnlimited,
      videoQuantity,
      creditType,
      creditBalance,
      financeBalance,
      organisationProduct,
      contractItemBalance,
      canCreateOrSubmitProject: canSubmit
    });
  };

  render() {
    const {
      children
    } = this.props;
    const {
      userTeams,
      isUnlimited,
      creditType,
      creditBalance,
      videoQuantity,
      organisationProduct,
      contractItemBalance,
      canCreateOrSubmitProject,
      financeBalance,
      orgTeams
    } = this.state;

    return (
      <CreditsContext.Provider
        value={creditsContextSelector({
          state: {
            userTeams,
            isUnlimited,
            creditType,
            creditBalance,
            videoQuantity,
            organisationProduct,
            contractItemBalance,
            canCreateOrSubmitProject,
            financeBalance,
            orgTeams
          },
          actions: {
            onCreditsUpdated: this.loadCreditsInformation
          }
        })}>

        {children}
      </CreditsContext.Provider>);

  }
}

export { CreditsProvider };
export default withFlagsConsumer(CreditsProvider);